import clsx, { type ClassValue } from "clsx";
import {
  ConsecutiveBreaker,
  circuitBreaker,
  handleAll,
  retry,
  wrap,
} from "cockatiel";
import type { FormEvent } from "react";
import {
  type CaseFileLite,
  type CaseFileUpload,
  CommunicationLanguage,
  CommunicationSource,
  DocumentSubType,
  type DocumentView,
  type FormInt,
  type FormIntQuestion,
  type Interrogatory,
  type ResponseInfo,
  ResponseRejectionReason,
  type ViewCommunicationItem,
} from "@/briefpoint-client";
import { router } from "@/lib/router";
import type {
  ErrorResponse,
  ExternalCommunicationsItem,
  FormIntResponse,
  ViewCommunicationResponse,
} from "@/lib/types";
import { CaliforniaId, NewYorkId } from "./constants";
import { isError, isErrorResponse } from "./type-guards";

/**
 * Utility function to merge class names using clsx
 */
export function cn(...inputs: ClassValue[]) {
  return clsx(inputs);
}

export interface FormIntExtended extends FormIntResponse {
  conditionalSkipsIds?: string[];
}

/**
 * Finds and organizes external communications for interrogatories that are marked for review.
 *
 * This function:
 * 1. Filters interrogatories to only those requested for review
 * 2. Matches external communications to each interrogatory
 * 3. Separates communications into attorney-created vs client-created items
 * 4. Handles conditional skip logic for Form Interrogatories (FROGs)
 * 5. Filters out skipped items based on conditional skip rules
 *
 * @param doc - The document view containing interrogatories to process
 * @param communication - The communication data containing external communication items and review requests
 * @param setData - React state setter to update the external communications data
 * @param formInterrogatories - Optional Form Interrogatory data used for conditional skip logic
 * @returns Array of external communications items, or undefined if no data is available
 */
export const findExternalComms = (
  doc: DocumentView,
  communication: ViewCommunicationResponse,
  formInterrogatories?: FormIntResponse[] | null,
): ExternalCommunicationsItem[] | undefined => {
  // Track IDs of interrogatories that should be skipped due to conditional logic
  let conditionalSkipsIds: string[] = [];

  // Filter interrogatories to only those requested for review, then map each to its communications
  const results: ExternalCommunicationsItem[] | undefined = doc?.interrogatories
    // Only include interrogatories that are in the requested review list
    ?.filter((x) =>
      communication?.communicationInfo?.requestedReviewIds?.find(
        (r) => r === x.id,
      ),
    )
    .map((interrogatory: Interrogatory) => {
      // Find all external communications that match this interrogatory's ID
      const matchingCommunications =
        communication?.externalCommunicationItems?.filter(
          (communication) =>
            interrogatory.id !== undefined &&
            communication.objectId === interrogatory.id.toString(),
        );

      // Separate communications into attorney-created vs client-created items
      let externalAttorneyItems: ViewCommunicationItem[] | undefined;
      let externalClientItems: ViewCommunicationItem[] | undefined;

      // Categorize each matching communication by creator
      for (const match of matchingCommunications ?? []) {
        if (match.createdBy === communication?.createdBy) {
          // Communication created by the attorney (same creator as the communication data)
          if (!externalAttorneyItems) {
            externalAttorneyItems = [match];
          } else {
            externalAttorneyItems = [...externalAttorneyItems, match];
          }
        } else {
          // Communication created by the client (different creator)
          if (!externalClientItems) {
            externalClientItems = [match];
          } else {
            externalClientItems = [...externalClientItems, match];
          }
        }
      }

      // Client items that are tasks: source is client and parent is an attorney item
      const attorneyIds = new Set(
        (matchingCommunications ?? [])
          .filter((m) => m.source === CommunicationSource.Attorney && m.id)
          .map((m) => m.id as string),
      );
      const externalClientTasks = (matchingCommunications ?? []).filter(
        (m) =>
          m.source === CommunicationSource.Client &&
          m.parentCommunicationId != null &&
          attorneyIds.has(m.parentCommunicationId),
      );

      // Handle Form Interrogatory (FROG) conditional skip logic
      if (formInterrogatories) {
        // Find the matching Form Interrogatory data for this interrogatory
        const formInt: FormIntExtended | undefined =
          formInterrogatories.find(
            (formData) => formData.id === interrogatory.text,
          ) ?? undefined;

        // Check if this form interrogatory has conditional skip logic that applies
        // Conditional skip applies when:
        // 1. The form interrogatory has conditionalSkipsEntireSection enabled
        // 2. The client's first communication response is "no"
        if (
          formInt?.conditionalSkipsEntireSection &&
          externalClientItems?.[0]?.communication === "no"
        ) {
          // Calculate which interrogatories should be skipped:
          // Skip all form interrogatories in the same section that come after this one
          formInt.conditionalSkipsIds = formInterrogatories
            .filter(
              (f) =>
                f.section === formInt?.section &&
                f.number &&
                formInt?.number &&
                f.number > formInt.number,
            )
            .map((f) => f.id as string);

          // Accumulate all conditional skip IDs across all interrogatories
          conditionalSkipsIds = [
            ...conditionalSkipsIds,
            ...formInt.conditionalSkipsIds,
          ];
        }

        // Return result with Form Interrogatory data included
        return {
          communicationId: communication.id,
          communicationStatus: communication.status,
          formInt,
          interrogatory,
          externalAttorneyItems,
          externalClientItems,
          externalClientTasks,
        };
      }

      // Return result without Form Interrogatory data (for non-FROG interrogatories)
      return {
        communicationId: communication.id,
        communicationStatus: communication.status,
        interrogatory,
        externalAttorneyItems,
        externalClientItems,
        externalClientTasks,
      };
    });

  // Apply conditional skip filtering if any skip IDs were identified
  if (conditionalSkipsIds.length > 0) {
    const filteredResults = results?.filter((item) => {
      // Exclude items whose interrogatory text matches a conditional skip ID
      return (
        item.interrogatory.text !== null &&
        item.interrogatory.text !== undefined &&
        !conditionalSkipsIds.includes(item.interrogatory.text)
      );
    });

    return filteredResults;
  }

  // No conditional skips to apply, return all results
  return results;
};

// strip <script> tags from strings containing HTML
export function sanitizeHtmlString(text?: string | null) {
  if (!text) {
    return "";
  }
  return text.replace(/<\/?script>/gi, "");
}

export async function promiseRetry<T>(
  fn: () => Promise<T>,
  retries: number,
  retryIntervalMilliseconds: number,
  previousError?: Error,
): Promise<T> {
  return !retries
    ? Promise.reject(previousError ?? new Error("Max retries reached"))
    : fn().catch(async (error) => {
        await new Promise((resolve) =>
          setTimeout(resolve, retryIntervalMilliseconds),
        );
        return promiseRetry(
          fn,
          retries - 1,
          retryIntervalMilliseconds,
          isError(error) ? error : undefined,
        );
      });
}

export function safeJsonParse<T = unknown>(str: string): T {
  try {
    return JSON.parse(str).trim();
  } catch {
    return str as T;
  }
}

export function formatTitle(input: string | null | undefined): string {
  let result = "";
  if (input) {
    for (let i = 0; i < input.length; i++) {
      // Add the first character as is
      if (i === 0) {
        result += input[i];
      } else {
        // Add space before capital letters
        if (input[i] === input[i].toUpperCase()) {
          result += " ";
        }
        result += input[i];
      }
    }
    return result;
  }

  return "";
}

export function formatTitleString(str: string | null | undefined) {
  switch (str) {
    // NOTE The backend will return a 'title' value of 'Interrogatory' for Special Interrogatory requests, FROGs will always be 'Form Interrogatory'
    // so we know it will always be a Special Interrogatory title in that case
    case "Request  for   Admission":
    case "Request for Admission":
      return "docList.titleRfa";
    case "Request  for   Production":
    case "Request for Production":
      return "docList.titleRfp";
    case "Special   Interrogatory":
    case "Special Interrogatory":
    case "Interrogatory":
      return "docList.titleSint";
    case "Form   Interrogatory":
    case "Form Interrogatory":
      return "docList.titleFrog";
    default:
      return "docList.titleReq";
  }
}

export function requestTitle(doc: DocumentView | undefined) {
  if (doc) {
    switch (doc.documentSubType) {
      case DocumentSubType.RequestsForAdmission:
        return doc.jurisdiction === NewYorkId
          ? "docList.titleNta"
          : "docList.titleRfa";
      case DocumentSubType.RequestsForProduction:
        return doc.jurisdiction === NewYorkId
          ? "docList.titleNdi"
          : "docList.titleRfp";
      case DocumentSubType.SpecialInterrogatorries:
        return doc.jurisdiction === CaliforniaId
          ? "docList.titleSint"
          : "docList.titleInt";
      case DocumentSubType.FormInterrogatorries:
        return "docList.titleFrog";
    }

    return "docList.titleReq";
  }

  return "docList.titleReq";
}

export function TryParseJsonToObject<T = unknown>(text: string): T[] {
  try {
    return (JSON.parse(text ?? "[]") ?? []) as T[];
  } catch {
    return [] as T[];
  }
}

function handleErrorResponse(error: ErrorResponse, logout: () => void) {
  setTimeout(() => {
    if (error.status === 403 || error.status === 404) {
      void router.navigate({ to: "/", replace: true });
    } else if (error.status >= 400 && error.status < 500) {
      if (error.status === 401) {
        logout();
      }
      void router.navigate({ to: "/", replace: true });
    }
  }, 1000);
}

// TODO: remove entirely.
export async function externalRequestWrapper<T>(
  fetchData: Promise<T>,
  attempts: number,
  logout: () => void,
): Promise<T> {
  // Attempt to retry if no response/timeout/network error occurs
  const retryPolicy = retry(handleAll, { maxAttempts: attempts });

  // Stops calling the executed function for 10 seconds if it fails 5 times in a row.
  const circuitBreakerPolicy = circuitBreaker(handleAll, {
    halfOpenAfter: 10 * 1000,
    breaker: new ConsecutiveBreaker(5),
  });

  const retryWithBreaker = wrap(retryPolicy, circuitBreakerPolicy);

  try {
    const result = await retryWithBreaker.execute(() => fetchData);
    return result;
  } catch (error: unknown) {
    // if the response is unauthorized/forbidden
    if (isErrorResponse(error)) {
      handleErrorResponse(error, logout);
      throw error;
    }
    throw new Error("Max limit of retries reached.");
  }
}

// Returns the first communication item in the current language,
// or the original language if not found,
// or the first item if no language is specified
export const getCommunicationItemInCurrentLanguage = (
  items: ViewCommunicationItem[] | undefined | null,
  language: string,
  originalOnly: boolean,
): ViewCommunicationItem | undefined => {
  if (!items) return undefined;

  return (
    items.find(
      (item) =>
        (!originalOnly || !item.isGenerated) &&
        item.language?.toLowerCase() === language,
    ) ??
    items.find(
      (item) =>
        (!originalOnly || !item.isGenerated) &&
        item.language === CommunicationLanguage.EN,
    ) ??
    items.find((item) => !originalOnly || !item.isGenerated)
  );
};

export const getLanguageEnum = (lang: string) => {
  switch (lang) {
    case "es":
      return CommunicationLanguage.ES;
    default:
      return CommunicationLanguage.EN;
  }
};

// Alias for backward compatibility
export const getItemInCurrentLanguage = getCommunicationItemInCurrentLanguage;

// The following functions are used to validate and parse the input data for the
// FrogResMultiAddField component to fix the data format for the additional responses.
const validateAndParseInput = (
  input: string | null | undefined,
): string[] | null => {
  if (input == null || typeof input !== "string") {
    return null;
  }

  try {
    const parsed = JSON.parse(input);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

// Detects if the input data is a nested array pattern to fix the data format for the additional responses.
export const detectNestedArrayPatternStrict = (
  input: string | null | undefined,
): boolean => {
  const parsed = validateAndParseInput(input);
  if (!parsed) return false;

  return parsed.every((element) => {
    if (element === null) return true;
    if (typeof element !== "string") return false;

    const trimmed = element.trim();
    return !(trimmed.startsWith("[") && trimmed.endsWith("]"));
  });
};

// Transforms the input data to a nested array pattern to fix the data format for the additional responses.
export const transformToNestedArrayPattern = (
  input: string | null | undefined,
): string | null => {
  const parsed = validateAndParseInput(input);
  if (!parsed) return null;

  const transformedInnerArray = [null, ...parsed];
  const innerArrayString = JSON.stringify(transformedInnerArray);
  return JSON.stringify([innerArrayString]);
};

/**
 * Converts any error type to a proper Error object with a stack trace.
 * Prevents "Script error" messages in Raygun by ensuring all errors have:
 * - Descriptive error messages
 * - Proper Error instance
 * - Stack trace
 */
export function normalizeError(error: unknown): Error {
  let result: Error;

  // Already an Error - return as-is
  if (error instanceof Error) {
    result = error;
  }
  // HTTP Response objects (from fetch/API failures)
  else if (error instanceof Response) {
    result = new Error(
      `HTTP ${error.status} ${error.statusText || "Error"}: ${error.url || "Unknown URL"}`,
    );
    result.name = "HTTPError";
  }
  // Plain objects
  else if (typeof error === "object" && error !== null) {
    // Object with message property - extract it
    if ("message" in error && typeof error.message === "string") {
      result = new Error(error.message);
      if ("name" in error && typeof error.name === "string") {
        result.name = error.name;
      }
    }
    // Other objects - serialize
    else {
      try {
        result = new Error(`Error object: ${JSON.stringify(error)}`);
        result.name = "SerializedError";
      } catch {
        result = new Error("[Circular reference or non-serializable data]");
        result.name = "UnserializableError";
      }
    }
  }
  // Primitives (string, number, boolean, null, undefined)
  else {
    result = new Error(String(error) || "Unknown error");
    result.name = "PrimitiveError";
  }

  // Ensure stack trace exists (prevents "Script error" in Raygun)
  if (!result.stack) {
    if (Error.captureStackTrace) {
      Error.captureStackTrace(result, normalizeError);
    } else {
      result.stack = `${result.name}: ${result.message}\n  at normalizeError`;
    }
  }

  return result;
}

export function getResponseNumber(
  doc: DocumentView | undefined,
  interrogatory?: Interrogatory,
) {
  return (
    Number(interrogatory?.number ?? 1) + (Number(doc?.startingNumber) ?? 1) - 1
  );
}

// ============================================================================
// Form Interrogatory (FROG) Response Utilities
// ============================================================================

/**
 * Checks if a response text is a valid JSON array.
 * @param responseText - The response text to check
 * @returns True if responseText is a JSON array, false otherwise
 */
export function isJsonArrayResponse(
  responseText: string | undefined | null,
): boolean {
  if (!responseText) return false;
  try {
    const parsed = JSON.parse(responseText);
    return Array.isArray(parsed);
  } catch {
    return false;
  }
}

/**
 * Determines if radio buttons should be shown for a form interrogatory.
 * Radio buttons are shown when conditionalAnswerDirection is defined (not undefined or null).
 * @param formInt - The form interrogatory data
 * @returns True if radio buttons should be shown, false otherwise
 */
export function shouldShowRadioButtons(
  formInt: FormInt | undefined | null,
): boolean {
  return (
    formInt?.conditionalAnswerDirection !== undefined &&
    formInt?.conditionalAnswerDirection !== null
  );
}

/**
 * Determines if the response form should be shown based on conditional logic.
 * @param options - Configuration options
 * @param options.showRadioButtons - Whether radio buttons are shown
 * @param options.isInverted - Whether the conditional logic is inverted
 * @param options.responseText - The current response text
 * @param options.isJsonArrayResponse - Whether the response is a JSON array
 * @returns True if the response form should be shown, false otherwise
 */
export function shouldShowResponseForm(options: {
  showRadioButtons: boolean;
  isInverted: boolean;
  responseText: string | undefined | null;
  isJsonArrayResponse: boolean;
}): boolean {
  const { showRadioButtons, isInverted, responseText, isJsonArrayResponse } =
    options;

  // Always show response form if radio buttons are not shown
  if (!showRadioButtons) {
    return true;
  }

  // If response is undefined or empty when radio buttons are present, hide the form
  if (!responseText || responseText === "") {
    return false;
  }

  if (isInverted) {
    // For inverted: show when response is "no", has text content (not "yes"), or is a JSON array
    return (
      responseText === "no" ||
      isJsonArrayResponse ||
      Boolean(responseText && responseText !== "yes" && responseText !== "")
    );
  }

  // For normal: show when response is NOT "no" and NOT '""'
  return responseText !== "no" && responseText !== '""';
}

/**
 * Gets the initial response value when "Yes" is selected.
 * @param options - Configuration options
 * @param options.isInverted - Whether the conditional logic is inverted
 * @param options.hasMultipleQuestions - Whether there are multiple questions
 * @param options.questions - Array of form questions
 * @param options.canAddAdditional - Whether additional responses can be added
 * @returns The initial response string for "Yes" selection
 */
export function getInitialResponseForYes(options: {
  isInverted: boolean;
  hasMultipleQuestions: boolean;
  questions: FormIntQuestion[];
  canAddAdditional?: boolean;
}): string {
  const { isInverted, hasMultipleQuestions, questions, canAddAdditional } =
    options;

  // For inverted: set response to "yes"
  if (isInverted) {
    return "yes";
  }

  // For normal: initialize response based on questions
  if (hasMultipleQuestions) {
    // Initialize as JSON array with empty strings for each question
    return JSON.stringify(questions.map(() => ""));
  }

  if (canAddAdditional) {
    // For canAddAdditional, initialize as array with one empty string
    return '[""]';
  }

  // Set to fake character to keep response true
  return "‎";
}

/**
 * Gets the initial response value when "No" is selected.
 * @param options - Configuration options
 * @param options.isInverted - Whether the conditional logic is inverted
 * @param options.hasMultipleQuestions - Whether there are multiple questions
 * @returns The initial response string for "No" selection
 */
export function getInitialResponseForNo(options: {
  isInverted: boolean;
  hasMultipleQuestions: boolean;
}): string {
  const { isInverted, hasMultipleQuestions } = options;

  // For inverted: clear the response array if there are questions
  if (isInverted) {
    if (hasMultipleQuestions) {
      return JSON.stringify("");
    }
    return "no";
  }

  // For normal: set response to "no"
  return "no";
}

/**
 * Gets the response text to pass to child forms, handling initialization logic.
 * @param options - Configuration options
 * @param options.responseText - The current response text
 * @param options.hasMultipleQuestions - Whether there are multiple questions
 * @param options.showResponseForm - Whether the response form should be shown
 * @param options.questions - Array of form questions
 * @param options.isJsonArrayResponse - Whether the response is a JSON array
 * @returns The response text to pass to child forms
 */
export function getResponseTextForForm(options: {
  responseText: string;
  hasMultipleQuestions: boolean;
  showResponseForm: boolean;
  questions: FormIntQuestion[];
  isJsonArrayResponse: boolean;
}): string {
  const {
    responseText,
    hasMultipleQuestions,
    showResponseForm,
    questions,
    isJsonArrayResponse,
  } = options;

  if (hasMultipleQuestions && showResponseForm) {
    // If responseText is "yes" or empty and we have multiple questions, initialize as JSON array
    if (
      responseText === "yes" ||
      responseText === "" ||
      responseText === '""'
    ) {
      return JSON.stringify(questions.map(() => ""));
    }
    // If it's already a JSON array, use it as-is
    if (isJsonArrayResponse) {
      return responseText;
    }
    // Otherwise, initialize as JSON array
    return JSON.stringify(questions.map(() => ""));
  }

  return responseText;
}

/**
 * Interface for keyed response items (used in AdditionalAnswersFrogResponseForm).
 */
export interface KeyedResponse {
  key: string;
  value: string | undefined;
}

/**
 * Interface for uploaded file information with id and name.
 */
export interface UploadedFileInfo {
  id: string;
  name: string | null;
}

/**
 * Extracts matching uploaded documents based on attachment IDs from external client items.
 *
 * This function:
 * 1. Extracts all attachment IDs from externalClientItems' responseInfo
 * 2. Matches those IDs with documents in uploadedDocuments
 * 3. Returns an array of objects containing only id and name
 *
 * @param externalClientItems - Array of external client communication items
 * @param uploadedDocuments - Array of uploaded documents to match against
 * @returns Array of UploadedFileInfo objects with id and name
 */
export function getMatchingUploadedDocuments(
  externalClientItems: ViewCommunicationItem[] | undefined | null,
  uploadedDocuments: CaseFileLite[] | undefined | null,
): UploadedFileInfo[] {
  // Extract attachment IDs from externalClientItems
  const attachmentIds: string[] = externalClientItems
    ? externalClientItems
        .flatMap((item) => item.responseInfo?.attachmentIds ?? [])
        .filter((id): id is string => typeof id === "string")
    : [];

  // Return empty array if no attachment IDs or no uploaded documents
  if (attachmentIds.length === 0 || !uploadedDocuments) {
    return [];
  }

  // Match documents by ID
  const attachmentIdSet = new Set(attachmentIds);
  const matchingDocuments = uploadedDocuments.filter(
    (doc): doc is CaseFileLite & { id: string } =>
      !!doc.id && attachmentIdSet.has(doc.id),
  );

  // Map to UploadedFileInfo format
  return matchingDocuments.map((doc) => ({
    id: doc.id,
    name: doc.name ?? null,
  }));
}

/**
 * Builds a ResponseInfo object from provided values.
 * Returns undefined if no values are provided.
 *
 * @param attachmentIds - Array of attachment IDs
 * @param responseRejectionReason - Rejection reason enum value
 * @param associatedObjectIds - Array of associated object IDs
 * @returns ResponseInfo object or undefined if no values provided
 */
export function buildResponseInfo(
  attachmentIds?: string[] | null,
  responseRejectionReason?: ResponseRejectionReason,
  associatedObjectIds?: string[] | null,
): ResponseInfo | undefined {
  if (
    !attachmentIds?.length &&
    !responseRejectionReason &&
    !associatedObjectIds?.length
  ) {
    return undefined;
  }

  return {
    ...(attachmentIds?.length && { attachmentIds }),
    ...(responseRejectionReason && { responseRejectionReason }),
    ...(associatedObjectIds?.length && { associatedObjectIds }),
  };
}

/**
 * Extracts current communication data from external communication items.
 * Gets the communication item in the current language and extracts relevant data.
 *
 * @param externalClientItems - Array of external client communication items
 * @param interrogatoryId - The interrogatory ID (fallback if not found in items)
 * @param language - Current language code
 * @returns Object containing responseText, responseInfo, externalClientCommunicationId, and interrogatoryId
 */
export function getCurrentCommunicationData(
  externalClientItems: ViewCommunicationItem[] | undefined | null,
  interrogatoryId: number,
  language: string,
): {
  responseText: string;
  responseInfo: ResponseInfo | undefined;
  externalClientCommunicationId: string | undefined;
  interrogatoryId: number;
} {
  const communicationItem = getCommunicationItemInCurrentLanguage(
    externalClientItems,
    language,
    true,
  );

  return {
    responseText: communicationItem?.communication ?? "",
    responseInfo: communicationItem?.responseInfo,
    externalClientCommunicationId: externalClientItems?.[0]?.id,
    interrogatoryId,
  };
}

/**
 * Parses a response string as a JSON array of KeyedResponse objects.
 * @param response - The response string to parse
 * @returns Array of KeyedResponse objects
 */
export function parseKeyedResponse(
  response: string | null | undefined,
): KeyedResponse[] {
  try {
    const parsed = (
      (JSON.parse(response ?? "null") ?? [undefined]) as (string | undefined)[]
    ).map((item: string | undefined, index: number) => ({
      key: index.toString(),
      value: item,
    }));
    return parsed;
  } catch {
    return [];
  }
}

/**
 * Generates a UUID v4 string.
 * @returns A UUID v4 string
 */
function generateUUID(): string {
  // Use crypto.randomUUID if available (modern browsers)
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Uploads files to Azure Blob Storage using a SAS URL and returns file metadata.
 * Similar to the uploadFilesToAzure function in document-builder-ui.
 * @param options - Upload configuration
 * @param options.files - Array of files to upload
 * @param options.uploadUrl - SAS URL for Azure Blob Storage upload
 * @param options.onProgress - Optional progress callback
 * @returns Array of CaseFileUpload objects with file metadata
 */
// TODO: this is only used in ResponseUpload and should be relocated to that component.
export async function uploadFilesToAzure({
  files,
  uploadUrl,
  onProgress,
}: {
  files: File[];
  uploadUrl: string;
  onProgress?: (percent: number) => void;
}): Promise<CaseFileUpload[]> {
  try {
    // Split the SAS URL to insert the upload key and filename
    const splitUrl = uploadUrl.split("?");
    if (splitUrl.length !== 2) {
      throw new Error("Invalid upload URL format");
    }

    // Generate random UUID for upload key
    const uploadKey = generateUUID();

    // Upload files to Azure Blob Storage and collect file info
    const uploadedFiles: CaseFileUpload[] = [];
    let totalProgress = 0;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      // SAS url is ".../{containerName}?querystring=" we need to insert the key and filename
      // so it looks like ".../{containerName}/{key}/{filename}?querystring="
      const blobUrl =
        splitUrl[0] +
        `/${uploadKey}/${encodeURIComponent(file.name)}` +
        "?" +
        splitUrl[1];

      // Convert File to ArrayBuffer for upload
      const arrayBuffer = await file.arrayBuffer();

      // Upload the file using PUT request
      const uploadResponse = await fetch(blobUrl, {
        method: "PUT",
        body: arrayBuffer,
        headers: {
          "x-ms-blob-type": "BlockBlob",
          "Content-Type": file.type || "application/octet-stream",
        },
      });

      if (!uploadResponse.ok) {
        throw new Error(
          `Failed to upload ${file.name}: ${uploadResponse.statusText}`,
        );
      }

      // Extract hash from response headers (Azure returns MD5 in x-ms-content-md5 header)
      const hashHeader = uploadResponse.headers.get("x-ms-content-md5");
      const hash = hashHeader || file.size.toString(); // Fallback to file size if no hash

      // Add to uploaded files array
      uploadedFiles.push({
        name: file.name,
        key: uploadKey,
        hash: hash,
      });

      // Update progress
      totalProgress = Math.round(((i + 1) * 100) / files.length);
      onProgress?.(totalProgress);
    }

    return uploadedFiles;
  } catch (error) {
    throw new Error(
      `Upload failed: ${error instanceof Error ? error.message : "Unknown error"}`,
    );
  }
}

/**
 * Default configuration for scroll adjustment hook used in response form fields.
 * This ensures consistent behavior across all form textarea fields.
 */
// TODO: move to constants.ts
export const DEFAULT_SCROLL_ADJUSTMENT_CONFIG = {
  offset: 100,
  behavior: "smooth" as const,
  debounceDelay: 50,
} as const;

/**
 * Gets the response rejection reason options for dropdowns.
 * @param t - Translation function from react-i18next
 * @returns Array of reason options with labels and values
 */
// TODO: should this be a hook?
export function getResponseRejectionReasonOptions(
  t: (key: string) => string,
): Array<{ label: string; value: ResponseRejectionReason }> {
  return [
    {
      label: t("wizardViews.uploadReasonStolen"),
      value: ResponseRejectionReason.Stolen,
    },
    {
      label: t("wizardViews.uploadReasonNoLongerInPossession"),
      value: ResponseRejectionReason.NoLongerInPossession,
    },
    {
      label: t("wizardViews.uploadReasonNeverInPossession"),
      value: ResponseRejectionReason.NeverInPossession,
    },
    {
      label: t("wizardViews.uploadReasonDestroyed"),
      value: ResponseRejectionReason.Destroyed,
    },
    {
      label: t("wizardViews.uploadReasonLost"),
      value: ResponseRejectionReason.Lost,
    },
    {
      label: t("wizardViews.uploadReasonNeverExisted"),
      value: ResponseRejectionReason.NeverExisted,
    },
    {
      label: t("wizardViews.uploadReasonUnknown"),
      value: ResponseRejectionReason.Unknown,
    },
  ];
}

/**
 * Creates a form submission handler that prevents default behavior and stops propagation.
 * This is a common pattern used across response form components, figured it might be worth to make it a utility function.
 * @param handleSubmit - The form's handleSubmit function from tanstack/react-form
 * @returns Event handler function for form onSubmit
 */
export function createFormSubmissionHandler(
  handleSubmit: () => void | Promise<void>,
) {
  return (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    void handleSubmit();
  };
}

export function pluralize(amount: number, text: string, printNumber = false) {
  return (printNumber ? `${amount} ` : "") + (amount === 1 ? text : `${text}s`);
}
